import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

import {
  QuoteV2Create,
  FirewallSide,
  PricingQuoteResponseOutput,
  QuoteV2Update,
  QuoteV2Get,
} from '../../lib/pricing-tool-api/index';
import { useQuoteService } from '../../lib/services/QuoteService';

export const foo = 'bar';

export function useSaveQuote(priceInfo: PricingQuoteResponseOutput | null) {
  const [memo, setMemo] = useState('');
  const [dealName, setDealName] = useState('');
  const [dealId, setDealId] = useState('');
  const [firewallSide, setFirewallSide] = useState<FirewallSide>(
    FirewallSide.Candidate,
  );
  const [clientName, setClientName] = useState('');
  const [savedQuote, setSavedQuote] = useState<QuoteV2Get | null>(null);
  const [isQuoteSaving, setIsQuoteSaving] = useState(false);
  const [quoteErrorMessage, setQuoteErrorMessage] = useState('');
  const { user } = useAuth0();
  const quoteService = useQuoteService();

  const canSaveQuote =
    !isQuoteSaving &&
    !!priceInfo &&
    dealName !== '' &&
    dealId !== '' &&
    clientName !== '';

  const saveQuote = async (onComplete: (quoteId: string) => void) => {
    setIsQuoteSaving(true);
    setQuoteErrorMessage('');

    try {
      if (!user?.email) {
        throw new Error('You must be logged in.');
      }

      if (!priceInfo) {
        throw new Error('No pricing information available to save.');
      }

      if (savedQuote) {
        // Updating
        const quoteUpdateData: QuoteV2Update = {
          price_info: priceInfo,
          client_name: clientName,
          deal_name: dealName,
          zendesk_deal_id: dealId,
          firewall_side: firewallSide,
          memo,
        };
        const response = await quoteService.updateQuoteV2(
          savedQuote.id,
          quoteUpdateData,
        );
        setSavedQuote(response.data);
        setQuoteErrorMessage('');
        onComplete(response.data.id);
      } else {
        const quoteCreateData: QuoteV2Create = {
          user_email: user.email,
          price_info: priceInfo,
          client_name: clientName,
          deal_name: dealName,
          zendesk_deal_id: dealId,
          firewall_side: firewallSide,
          memo: memo || undefined,
        };

        const response = await quoteService.createQuoteV2(quoteCreateData);
        setSavedQuote(response.data);
        setQuoteErrorMessage('');
        onComplete(response.data.id);
      }
    } catch (e) {
      let msg = '';
      if (typeof e === 'string') {
        msg = e;
      } else if (e instanceof Error) {
        msg = e.message;
      } else {
        msg = 'Unknown error occurred while saving quote';
      }
      setQuoteErrorMessage(msg);
    }
    setIsQuoteSaving(false);
  };
  return {
    memo,
    dealName,
    dealId,
    firewallSide,
    clientName,
    setMemo,
    setDealName,
    setDealId,
    setFirewallSide,
    setClientName,
    savedQuote,
    setSavedQuote,
    isQuoteSaving,
    quoteErrorMessage,
    setQuoteErrorMessage,
    saveQuote,
    canSaveQuote,
  };
}
